import { useIntl } from '@/intl'
import {
  mqMin,
  space,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import {
  Icon,
  IconGlyph,
  Sheet,
  Typography,
  Typography as TypographyUds,
} from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

interface CheckoutSectionMoreInformationProps {
  title: string
  description?: string
  descriptionElement?: React.ReactNode
  buttonAriaLabel?: string | undefined
  readMoreLabel?: string | undefined
}

interface CheckoutSectionProps {
  title: string
  description?: string
  glyph?: IconGlyph
  children?: React.ReactNode
  moreInformationTitle?: string | undefined
  moreInformationDescription?: string | undefined
  moreInformationDescriptionElement?: React.ReactNode | undefined
  moreInformationButtonAriaLabel?: string | undefined
  moreInformationReadMoreLabel?: string | undefined
}

const Wrapper = styled.div`
  background: ${tok('semantics_views_background_color')};
  padding: ${space('primitives_space_16')};
  border-radius: ${tokPx('primitives_corner_radius_none')};
  border-top: ${tokPx('semantics_boxes_border_width')} solid ${tok('semantics_boxes_border_color')};
  border-bottom: ${tokPx('semantics_boxes_border_width')} solid ${tok('semantics_boxes_border_color')};
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
  margin-left: -${space('primitives_space_16')};
  margin-right: -${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-radius: ${tokPx('semantics_boxes_corner_radius')};
    border: ${tokPx('semantics_boxes_border_width')} solid ${tok('semantics_boxes_border_color')};
    padding: ${space('primitives_space_20')};
    margin-left: ${space('primitives_space_0')};
    margin-right: ${space('primitives_space_0')};
  }
`

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};
`
const Title = styled(Typography)`
  ${Typography.modVariant('display-h-regular')};
  color: ${tok('semantics_content_color')};

  @media ${mqMin('primitives_breakpoint_l_min')} {
    ${Typography.modVariant('display-g-regular')};
  }
`
export const Description = styled.div`
  max-width: 640px;

  ul {
    padding-left: ${space('primitives_space_16')};
    margin-top: ${space('primitives_space_0')};
    margin-bottom: ${space('primitives_space_0')};
  }

  ${Typography} {
    ${Typography.modVariant('body-m-regular-snug')};
    color: ${tok('semantics_content_color')};
  }
`
const Body = styled.div`
  margin-top: ${space('primitives_space_4')};
`

const SheetContent = styled(Sheet.Content)`
  ${Typography.modVariant('body-l-regular-snug')}
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};

  *:first-child {
    margin-top: 0;
  }
  *:last-child {
    margin-bottom: 0;
  }
  table {
    width: 100%;
  }
  details{
    summary{
      ${TypographyUds.modVariant('body-m-bold-tight')}
      display: inline;
      color: ${tok('semantics_buttons_transparent_primary_content_color')};
      cursor: pointer;
    }
    &[open] summary {
      display: none;
    }
`

const ReadMore = styled.a`
  padding: 0;
  margin: 0;
  text-decoration: underline;
`

export default function CheckoutSection({
  children,
  title,
  description,
  glyph,
  moreInformationTitle,
  moreInformationDescription,
  moreInformationDescriptionElement,
  moreInformationButtonAriaLabel,
  moreInformationReadMoreLabel,
}: CheckoutSectionProps) {
  return (
    <Wrapper data-qa="checkout-service">
      {(title || moreInformationTitle || glyph) && (
        <Heading>
          {glyph && <Icon glyph={glyph} size={24} />}
          <Title as="h3">
            {title}
          </Title>
        </Heading>
      )}
      { (description || (moreInformationTitle && (moreInformationDescription || moreInformationDescriptionElement))) && (
        <Description>
          {description && (
            <Typography dangerouslySetInnerHTML={{ __html: description }} />
          )}
          {moreInformationTitle && (moreInformationDescription || moreInformationDescriptionElement) && (
            <MoreInformationSheet
              title={moreInformationTitle}
              description={moreInformationDescription}
              descriptionElement={moreInformationDescriptionElement}
              buttonAriaLabel={moreInformationButtonAriaLabel}
              readMoreLabel={moreInformationReadMoreLabel}
            />
          )}
        </Description>
      )}

      <Body>{children}</Body>
    </Wrapper>
  )
}

function MoreInformationSheet({ title, description, descriptionElement, buttonAriaLabel, readMoreLabel }: CheckoutSectionMoreInformationProps) {
  const intl = useIntl()

  return (
    <Sheet.Root>
      <Sheet.Trigger>
        <Typography>
          {' '}
          <ReadMore as="button" aria-label={buttonAriaLabel}>{readMoreLabel ?? intl.formatMessage({ id: 'checkout_steps_read_more' })}</ReadMore>
        </Typography>
      </Sheet.Trigger>
      <Sheet.Body side="right" hasPadding bottomSideInMobile>
        <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={title} />
        <SheetContent>
          {description && <div dangerouslySetInnerHTML={{ __html: description }} />}
          {descriptionElement}
        </SheetContent>
      </Sheet.Body>
    </Sheet.Root>
  )
}
